import React, { useEffect, useState } from 'react';
import { Button, Col, Drawer, Form, InputNumber, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { getStoreType } from 'store/requests/taskPlans';
import { ECOM_STATUS } from 'helpers/constants';

const { Option } = Select;

const StoreDetailsFilter = ({ isOpen, onClose, onApply, form }) => {
  const { t } = useTranslation();
  const [storeTypes, setStoreTypes] = useState([]);
  const [loadingStoreTypes, setLoadingStoreTypes] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const checkHasActiveFilters = (values) => {
    const hasValues =
      (values.type !== undefined && values.type !== null) ||
      (values.ecomStatus !== undefined && values.ecomStatus !== null) ||
      (values.active !== undefined && values.active !== null) ||
      (values.minM2 !== undefined && values.minM2 !== null) ||
      (values.maxM2 !== undefined && values.maxM2 !== null);
    setHasActiveFilters(!!hasValues);
  };

  useEffect(() => {
    if (isOpen) {
      fetchStoreTypes();
      checkHasActiveFilters(form.getFieldsValue());
    }
  }, [isOpen]);

  const fetchStoreTypes = async () => {
    setLoadingStoreTypes(true);
    try {
      const response = await getStoreType('page=0&size=25&sort=id');
      setStoreTypes(response?.content || []);
    } catch {
      setStoreTypes([]);
    } finally {
      setLoadingStoreTypes(false);
    }
  };

  const handleFinish = (values) => {
    onApply(values);
  };

  const handleReset = () => {
    form.resetFields();
    setHasActiveFilters(false);
    onApply({});
  };

  return (
    <Drawer
      placement="right"
      title={t('filters')}
      closable
      open={isOpen}
      onClose={onClose}
    >
      <Form
        form={form}
        onFinish={handleFinish}
        onValuesChange={(_, allValues) => checkHasActiveFilters(allValues)}
        layout="vertical"
        style={{ paddingTop: 20 }}
      >
        <Form.Item name="type" label={t('storeType')}>
          <Select
            placeholder={t('select')}
            loading={loadingStoreTypes}
            allowClear
            style={{ width: '100%' }}
          >
            {storeTypes.map((type) => (
              <Option key={type.name} value={type.name}>
                {type.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="ecomStatus" label={t('ecomStatus')}>
          <Select
            placeholder={t('select')}
            allowClear
            showSearch
            filterOption={true}
            optionFilterProp="children"
            style={{ width: '100%' }}
          >
            {Object.keys(ECOM_STATUS).map((key) => (
              <Option key={key} value={key}>
                {t(key)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="active" label={t('storeStatus')}>
          <Select placeholder={t('select')} allowClear style={{ width: '100%' }}>
            <Option value={true}>{t('OPEN')}</Option>
            <Option value={false}>{t('CLOSED')}</Option>
          </Select>
        </Form.Item>

        <Form.Item label={t('m2Info')} style={{ marginBottom: 0 }}>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item
                name="minM2"
                dependencies={['maxM2']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const max = getFieldValue('maxM2');
                      if (max !== undefined && max !== null && (value === undefined || value === null)) {
                        return Promise.reject(new Error(t('required')));
                      }
                      if (value !== undefined && value !== null && max !== undefined && max !== null && value > max) {
                        return Promise.reject(new Error(t('minCannotBeGreaterThanMax')));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber
                  placeholder="Min"
                  style={{ width: '100%' }}
                  min={0}
                  type="number"
                  precision={0}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="maxM2"
                dependencies={['minM2']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const min = getFieldValue('minM2');
                      if (min !== undefined && min !== null) {
                        if (value === undefined || value === null) {
                          return Promise.reject(new Error(t('required')));
                        }
                        if (value < min) {
                          return Promise.reject(new Error(t('maxCannotBeLessThanMin')));
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber
                  placeholder="Max"
                  style={{ width: '100%' }}
                  min={0}
                  type="number"
                  precision={0}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>

        <Row gutter={8} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Button block type="primary" htmlType="submit">
              {t('show')}
            </Button>
          </Col>
          <Col span={12}>
            <Button block onClick={handleReset} disabled={!hasActiveFilters}>
              {t('clear')}
            </Button>
          </Col>
        </Row>
      </Form>
    </Drawer>
  );
};

export default StoreDetailsFilter;
